"use client";

import { Suspense, useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { signInToAccount, requestAccountPasswordReset } from "@aluume/shared/lib/auth/account";
import { SiteHeader } from "@/components/SiteHeader";

const INPUT = "border-b border-ink/20 bg-transparent pb-2 text-[15px] text-ink-soft outline-none focus:border-ink w-full";

const LINK_ERRORS: Record<string, string> = {
  link_invalid: "That link is invalid. Request a new password reset below.",
  link_expired: "That link has expired. Request a new password reset below.",
};

// THE sign-in form for the customer app — one form, one credential check, for travel agents
// and "100 by Aluume" founder members alike.
//
// WHY IT LIVES AT /login AND NOT /account/login. This is the URL a person is asked to type,
// read down a phone, or find in a bookmark bar, and /account/login is one segment of nesting
// that buys nothing: there is no second thing under /account that a sign-in belongs beneath —
// /account is itself only the post-credential resolver ("agent, founder, both, or neither?").
// The form was never a child of that resolver; it runs BEFORE it.
//
// /account/login, /agent/login and /member/login are now thin redirect shims pointing here
// (page-level redirect(), NOT a next.config redirects() rule — see the shims for why), so
// every link already out in the world keeps working: welcome emails carrying ?email=&pw=,
// the /auth/callback and /auth/confirm link-expired bounces, and bookmarks.
//
// The copy is deliberately role-NEUTRAL. It used to be the agent form verbatim — "Sign in to
// book on account and view your statements" — which is not what a founder is signing in to
// do, and there is no way for this screen to know which one is typing: the only thing it
// holds is an email address and a password. Which portal you land in is decided AFTER the
// credential check, by /account, against the live `agents` / `members` rows.
//
// Query params are read with useSearchParams rather than parsed out of window.location in an
// effect: they are render input, not a side effect, so the first paint is already correct
// instead of flashing an empty form and filling it in after hydration.
function LoginInner() {
  const sp = useSearchParams();
  const linkError = sp.get("error");
  // Where to go after sign-in. Defaults to /account, which resolves who this is and forwards
  // to the right portal. A `next` from the middleware ("/agent/bookings") is honoured, but
  // only when it is a same-origin relative path — never an absolute URL or a protocol-relative
  // "//evil.example", which would make this form an open redirect.
  const nextParam = sp.get("next");
  const nextPath = nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : "/account";
  const emailParam = sp.get("email") ?? "";
  const pwParam = sp.get("pw") ?? "";

  const [email, setEmail] = useState(emailParam);
  const [password, setPassword] = useState(pwParam);
  const [error, setError] = useState(linkError && LINK_ERRORS[linkError] ? LINK_ERRORS[linkError] : "");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);
  const autoTried = useRef(false);

  async function signInWith(emailArg: string, pwArg: string) {
    setBusy(true); setError(""); setNotice("");
    try {
      const session = await signInToAccount(emailArg, pwArg);
      // Accounts provisioned by staff start on a temp password. Send them to /account, which
      // knows (server-side, from the linked row) that a password change is outstanding and
      // forwards to the set-password screen. This screen deliberately does NOT make that
      // decision itself: it would have to guess the role from a JWT claim to do it.
      if (session?.user?.user_metadata?.must_change_password) {
        window.location.assign("/account");
        return;
      }
      window.location.assign(nextPath);
    } catch (e) {
      setError((e as Error).message || "Couldn’t sign in. Check your details and try again.");
      setBusy(false);
    }
  }

  // Arrived from a welcome email carrying both credentials → sign in for them. Guarded by a
  // ref so a re-render can't fire a second attempt.
  useEffect(() => {
    if (autoTried.current || !emailParam || !pwParam || linkError) return;
    autoTried.current = true;
    void signInWith(emailParam, pwParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailParam, pwParam, linkError]);

  // GUARDED, AND NOT ONLY FOR TIDINESS. Each request mints a fresh recovery token and
  // INVALIDATES the previous one, so a double-tap sends two emails and kills the link in the
  // first — the member then opens the one that arrived first and is told it has expired. The
  // guard is the fix for that, not the spinner.
  async function forgot() {
    if (forgotBusy) return;
    if (!email) { setError("Enter your email first, then tap Forgot password."); return; }
    setForgotBusy(true); setError(""); setNotice("");
    try {
      await requestAccountPasswordReset(email);
      setNotice("If that email has an account, a reset link is on its way. Check your inbox.");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setForgotBusy(false);
    }
  }

  return (
    <main className="flex min-h-screen flex-col bg-cream">
      <SiteHeader />

      <div className="flex flex-1 items-center justify-center px-6 py-16">
        <div className="flex w-full max-w-[400px] flex-col">
          <h3 className="mb-2 text-center">Welcome back</h3>
          <p className="body mb-9 text-center text-ink-soft">
            Sign in to your Aluume account. We’ll take you to the right place.
          </p>

          <form
            className="flex flex-col gap-5"
            onSubmit={(e) => { e.preventDefault(); if (!busy && !forgotBusy && email && password) void signInWith(email, password); }}
          >
            <label className="flex flex-col gap-1.5">
              <span className="ui-label text-ink/50">Email</span>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className={INPUT}
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="ui-label text-ink/50">Password</span>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                className={INPUT}
              />
            </label>

            <button
              type="submit"
              disabled={busy || forgotBusy || !email || !password}
              className="ui-label mt-3 rounded-pill bg-ink py-3.5 text-ink-on-dark transition-colors hover:bg-accent disabled:opacity-50"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <button
            type="button"
            onClick={forgot}
            disabled={forgotBusy || busy}
            className="caption mt-4 text-center text-ink/65 underline-offset-2 hover:underline disabled:no-underline disabled:opacity-50"
          >
            {forgotBusy ? "Sending…" : "Forgot password?"}
          </button>

          {error && <p className="caption mt-4 text-center text-corten">{error}</p>}
          {notice && <p className="caption mt-4 text-center text-ink-soft">{notice}</p>}

          {/* One route out, not two. This used to end "…reservations to be set up, or see 100
              by Aluume" — which sold a membership to someone who is standing at a locked door,
              and named an internal team ("reservations") they have no way to reach. The link
              is the marketing site's own contact page (the canonical /contact-us in
              aluume.com's sitemap; the header/footer carry no contact-page href, only a tel:),
              opened in a new tab like every other aluume.com link in this app so a part-typed
              sign-in isn't thrown away. */}
          <p className="caption mt-11 text-center text-ink-soft/80">
            No account yet?{" "}
            <a
              href="https://www.aluume.com/contact-us"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2"
            >
              Contact Aluume
            </a>.
          </p>
        </div>
      </div>
    </main>
  );
}

export default function Login() {
  return (
    <Suspense fallback={<main className="min-h-screen bg-cream" />}>
      <LoginInner />
    </Suspense>
  );
}
